import React, { useContext, useMemo } from "react";

import styled from "@emotion/styled";

import Image from "../../../../static/img/favicon-96x96.png";
import { DataContext } from "../../../providers/VacancyDataProvider";
import JobDetail from "../../shared/Elements/JobDetail";
import JsonLd from "../../shared/JsonLd";
import Button from "../../shared/NewForms/Button";
import SEO from "../../shared/SEO";
import Spinner from "../../shared/Spinner";
import Error404 from "../errors/Error404";
import { getCategoryImage } from "./categoryImages";
import rating from "./rating.json";
import VacancySoft404 from "./VacancySoft404";

const TextContainer = styled.div`
    ${({ theme }) => theme.paddings.containerOuterInner.left};
    ${({ theme }) => theme.paddings.containerOuterInner.right};

    color: ${({ theme }) => theme.colors.blue.toString()};
    margin: 50px 0;
`;

const Container = styled.div`
    margin-bottom: 100px;
`;

export default function Vacancy({ page: { meta }, id }) {
    const dataContext = useContext(DataContext);
    const { vacancies, freshData, loading } = dataContext.data;

    const staticVacancy = useMemo(
        () => (loading ? null : vacancies.find((v) => v.id === id)),
        [loading, vacancies, id]
    );
    const freshVacancy = useMemo(
        () =>
            loading || !freshData?.vacancies
                ? null
                : freshData?.vacancies.find((v) => v.id === id),
        [loading, freshData, id]
    );

    const vacancy = freshVacancy || staticVacancy;

    if (loading) {
        return <Spinner />;
    }

    if (staticVacancy && !freshVacancy && freshData !== null) {
        return <VacancySoft404 vacancy={staticVacancy} />;
    }

    if (!staticVacancy && !freshVacancy) {
        return <Error404 />;
    }

    const slug = `stellenangebote/${vacancy.id}`;
    const categoryImage = getCategoryImage(vacancy);
    const JsonLdData = () => {
        if (vacancy.audiences.some((a) => a.id === "00001")) {
            return {
                "@context": "https://schema.org/",
                "@id": `https://www.hoyer.de/${slug}`,
                "@type": "EducationalOccupationalProgram",
                title: vacancy.title || vacancy.external_posting_name,
                description: vacancy.job_ad_text,
                image: categoryImage,
                startDate: vacancy.job_start_date,
                provider: {
                    "@type": "Organization",
                    name: vacancy.company_name,
                    logo: Image,
                    aggregateRating: {
                        ratingValue: rating.ratingValue,
                        reviewCount: rating.reviewCount,
                        bestRating: rating.bestRating,
                        worstRating: rating.worstRating,
                    },
                },
            };
        } else {
            return {
                "@context": "https://schema.org/",
                "@id": `https://www.hoyer.de/${slug}`,
                "@type": "JobPosting",
                title: vacancy.title || vacancy.external_posting_name,
                description: vacancy.job_ad_text,
                image: categoryImage,
                datePosted: vacancy.posting_last_updated_date,
                employmentType: "FULL_TIME",
                hiringOrganization: {
                    "@type": "Organization",
                    name: vacancy.company_name,
                    logo: Image,
                    aggregateRating: {
                        ratingValue: rating.ratingValue,
                        reviewCount: rating.reviewCount,
                        bestRating: rating.bestRating,
                        worstRating: rating.worstRating,
                    },
                },
                jobLocation: {
                    "@type": "Place",
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: vacancy.geo_street,
                        addressLocality: vacancy.geo_city,
                        addressRegion: vacancy.geo_state,
                        postalCode: vacancy.geo_zip,
                        addressCountry: {
                            "@type": "Country",
                            name:
                                vacancy.geo_country === "Deutschland"
                                    ? "de"
                                    : vacancy.geo_country,
                        },
                    },
                },
            };
        }
    };

    return (
        <>
            <JsonLd>{JsonLdData()}</JsonLd>

            <SEO lang="de" {...{ slug, ...meta }} templateFields={vacancy} />

            <Container>
                <JobDetail vacancy={vacancy} img={categoryImage} />
                <TextContainer
                    dangerouslySetInnerHTML={{ __html: vacancy.job_ad_text }}
                />
                <Button
                    to={vacancy.apply_online_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    centered
                >
                    Einfach online bewerben
                </Button>
            </Container>
        </>
    );
}
